import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  ArrowLeft,
  CurrencyDollar,
  Receipt,
  Warning,
  Clock,
  CalendarBlank,
  CaretDown,
  Download,
  ArrowRight,
} from "phosphor-react";
import { formatCurrency } from "../utils/formatters";
import { format, subDays, parseISO } from "date-fns";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function ReportsMoney() {
  const navigate = useNavigate();
  const [aging, setAging] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedBucket, setExpandedBucket] = useState(null);
  const [dateRange, setDateRange] = useState({
    start: format(subDays(new Date(), 30), "yyyy-MM-dd"),
    end: format(new Date(), "yyyy-MM-dd"),
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const startISO = new Date(dateRange.start).toISOString();
      const endISO = new Date(dateRange.end + "T23:59:59").toISOString();
      
      const [agingRes, summaryRes] = await Promise.all([
        fetch(`${API}/reports/invoice-aging`, { credentials: "include" }),
        fetch(`${API}/reports/summary?start_date=${startISO}&end_date=${endISO}`, { credentials: "include" }),
      ]);
      
      if (agingRes.ok) setAging(await agingRes.json());
      if (summaryRes.ok) setSummary(await summaryRes.json());
    } catch (error) {
      console.error("Error fetching money reports:", error);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const exportCSV = (bucketName, invoices) => {
    if (!invoices || invoices.length === 0) return;
    
    const headers = ["Invoice #", "Customer", "Balance Due", "Due Date", "Days Overdue"];
    const rows = invoices.map((inv) => [
      inv.invoice_number,
      inv.customer_name,
      inv.balance_due,
      inv.due_date ? format(parseISO(inv.due_date), "MM/dd/yyyy") : "",
      inv.days_overdue,
    ]);
    
    const csv = [headers, ...rows].map((row) => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `invoice-aging-${bucketName}-${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
  };

  const bucketLabels = {
    current: "Current",
    "1_30": "1-30 Days",
    "31_60": "31-60 Days",
    "61_90": "61-90 Days",
    over_90: "Over 90 Days",
  };

  const bucketColors = {
    current: "bg-green-100 text-green-700 border-green-200",
    "1_30": "bg-amber-100 text-amber-700 border-amber-200",
    "31_60": "bg-orange-100 text-orange-700 border-orange-200",
    "61_90": "bg-red-100 text-red-700 border-red-200",
    over_90: "bg-red-200 text-red-800 border-red-300",
  };

  if (loading && !aging) {
    return (
      <div className="space-y-6" data-testid="reports-money-page">
        <div className="animate-pulse">
          <div className="h-8 bg-zinc-200 rounded w-48 mb-6" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-24 bg-zinc-200 rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-money-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/reports")} data-testid="back-to-reports">
            <ArrowLeft size={18} />
          </Button>
          <div>
            <h1 className="font-heading text-2xl font-bold">Money Reports</h1>
            <p className="text-sm text-zinc-500">Revenue, outstanding, and invoice aging</p>
          </div>
        </div>
        
        {/* Date Range Selector */}
        <div className="flex items-center gap-2 bg-zinc-50 border border-zinc-200 rounded-lg p-2">
          <CalendarBlank size={18} className="text-zinc-500" />
          <input
            type="date"
            value={dateRange.start}
            onChange={(e) => setDateRange((prev) => ({ ...prev, start: e.target.value }))}
            className="bg-transparent border-none text-sm focus:outline-none"
          />
          <span className="text-zinc-400">to</span>
          <input
            type="date"
            value={dateRange.end}
            onChange={(e) => setDateRange((prev) => ({ ...prev, end: e.target.value }))}
            className="bg-transparent border-none text-sm focus:outline-none"
          />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-2 border-green-200 bg-green-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <CurrencyDollar size={18} className="text-green-600" />
              <span className="text-xs text-green-700 uppercase font-medium">Revenue (Period)</span>
            </div>
            <p className="font-heading text-2xl font-bold text-green-800">
              {formatCurrency(summary?.revenue?.total || 0)}
            </p>
            <p className="text-sm text-green-600 mt-1">{summary?.revenue?.payment_count || 0} payments</p>
          </CardContent>
        </Card>
        
        <Card className="border-2 border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Receipt size={18} className="text-blue-600" />
              <span className="text-xs text-blue-700 uppercase font-medium">Invoiced (Period)</span>
            </div>
            <p className="font-heading text-2xl font-bold text-blue-800">
              {formatCurrency(summary?.invoiced?.total || 0)}
            </p>
            <p className="text-sm text-blue-600 mt-1">{summary?.invoiced?.invoice_count || 0} invoices</p>
          </CardContent>
        </Card>
        
        <Card className="border-2 border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Clock size={18} className="text-amber-600" />
              <span className="text-xs text-amber-700 uppercase font-medium">Outstanding</span>
            </div>
            <p className="font-heading text-2xl font-bold text-amber-800">
              {formatCurrency(aging?.summary?.total_outstanding || 0)}
            </p>
            <p className="text-sm text-amber-600 mt-1">{aging?.summary?.total_invoices || 0} invoices</p>
          </CardContent>
        </Card>
        
        <Card className="border-2 border-red-200 bg-red-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Warning size={18} className="text-red-600" />
              <span className="text-xs text-red-700 uppercase font-medium">Overdue</span>
            </div>
            <p className="font-heading text-2xl font-bold text-red-800">
              {formatCurrency(summary?.overdue?.total || 0)}
            </p>
            <p className="text-sm text-red-600 mt-1">{summary?.overdue?.invoice_count || 0} invoices</p>
          </CardContent>
        </Card>
      </div>

      {/* Invoice Aging Table */}
      <Card className="border-2 border-zinc-200">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-lg">Invoice Aging</CardTitle>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => exportCSV("all", Object.values(aging?.buckets || {}).flatMap(b => b.invoices))}
              data-testid="export-aging-csv"
            >
              <Download size={14} className="mr-1" /> Export All
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {aging?.buckets && Object.entries(aging.buckets).map(([key, bucket]) => (
              <div key={key} className="border border-zinc-200 rounded-lg overflow-hidden">
                {/* Bucket Header */}
                <button
                  className={`w-full px-4 py-3 flex items-center justify-between ${
                    bucket.count > 0 ? "hover:bg-zinc-50 cursor-pointer" : "cursor-default"
                  }`}
                  onClick={() => bucket.count > 0 && setExpandedBucket(expandedBucket === key ? null : key)}
                  data-testid={`aging-bucket-${key}`}
                >
                  <div className="flex items-center gap-3">
                    <Badge className={bucketColors[key]}>{bucketLabels[key]}</Badge>
                    <span className="text-sm text-zinc-600">{bucket.count} invoices</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="font-heading font-bold">{formatCurrency(bucket.total)}</span>
                    {bucket.count > 0 && (
                      <CaretDown 
                        size={18} 
                        className={`text-zinc-400 transition-transform ${expandedBucket === key ? "rotate-180" : ""}`} 
                      />
                    )}
                  </div>
                </button>
                
                {/* Expanded Invoice List */}
                {expandedBucket === key && bucket.invoices.length > 0 && (
                  <div className="border-t border-zinc-200 bg-zinc-50">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-xs text-zinc-500 uppercase">
                            <th className="px-4 py-2">Invoice #</th>
                            <th className="px-4 py-2">Customer</th>
                            <th className="px-4 py-2 text-right">Balance</th>
                            <th className="px-4 py-2">Due Date</th>
                            <th className="px-4 py-2 text-right">Days</th>
                            <th className="px-4 py-2"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {bucket.invoices.slice(0, 10).map((inv) => (
                            <tr key={inv.invoice_id} className="border-t border-zinc-200 hover:bg-white">
                              <td className="px-4 py-2 font-medium">{inv.invoice_number}</td>
                              <td className="px-4 py-2">{inv.customer_name}</td>
                              <td className="px-4 py-2 text-right font-medium">{formatCurrency(inv.balance_due)}</td>
                              <td className="px-4 py-2 text-zinc-500">
                                {inv.due_date ? format(parseISO(inv.due_date), "MMM d, yyyy") : "-"}
                              </td>
                              <td className="px-4 py-2 text-right">
                                {inv.days_overdue > 0 ? (
                                  <span className="text-red-600">{inv.days_overdue}d</span>
                                ) : (
                                  <span className="text-green-600">Current</span>
                                )}
                              </td>
                              <td className="px-4 py-2">
                                <Link 
                                  to={`/invoices?id=${inv.invoice_id}`}
                                  className="text-blue-600 hover:text-blue-700 text-xs"
                                >
                                  View →
                                </Link>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {bucket.invoices.length > 10 && (
                      <div className="px-4 py-2 border-t border-zinc-200 text-sm text-zinc-500">
                        Showing 10 of {bucket.invoices.length} invoices
                        <Button 
                          variant="link" 
                          size="sm" 
                          onClick={() => exportCSV(key, bucket.invoices)}
                          className="ml-2"
                        >
                          Export all
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
